/**
 * EDITABLE FIELD — Componente inline edit riusabile.
 *
 * Sostituisce il pattern "pulsante Modifica -> form dedicato" con:
 *   view -> doppio click -> edit inline -> blur/Enter -> commit API -> view
 *
 * Stato finite-state:
 *   view -> edit -> saving -> view (success)
 *                         -> serverError (failure) -> edit/cancel
 *
 * Variants supportati: text, textarea, number, date, datetime, select, switch, lookup
 *
 * Accessibilita': role=button + tabIndex=0 in view mode, Enter/Space per entrare
 * in edit mode via tastiera, Esc per cancellare, aria-invalid per errori.
 *
 * Performance: React.memo sul componente esterno. Il re-render avviene solo se
 * cambia `value` prop o se lo stato interno (mode) cambia.
 *
 * Uso tipico:
 *   <EditableField
 *     value={person.firstName}
 *     variant={{ kind: 'text' }}
 *     required
 *     onCommit={async (newValue) => {
 *       await dispatch(updatePerson({ ...person, firstName: newValue as string })).unwrap();
 *       toast({ severity: 'success', message: t('common.saved') });
 *     }}
 *   />
 */

import {
  memo,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { meterStep } from './lookupMeter';
import TextField from '@mui/material/TextField';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import {
  useAsyncAutocomplete,
  datetimeLocalToIso,
  isoToDatetimeLocal,
  formatDateTime,
} from '@crm/shared';
import { DualFieldAutocomplete } from './DualFieldAutocomplete';

// ---------------------------------------------------------------------------
// Tipi pubblici
// ---------------------------------------------------------------------------

/**
 * Opzione di un autocomplete `lookup`.
 *
 * `id` è `number | string` perché non tutte le chiavi del CRM sono id Liferay: i codici
 * articolo del gestionale (`TS2010PI`) sono chiavi a tutti gli effetti, e sono stringhe. I
 * due call site numerici — assegnatario di un task, referente di un preventivo — non ne
 * risentono: `number` resta assegnabile alla union.
 */
export interface LookupOption {
  id: number | string;
  label: string;
  /**
   * Quanto l'opzione è «forte», fra 0 e 1, disegnato come una barretta colorata
   * accanto all'etichetta. Assente: nessuna barretta, e l'opzione si rende come
   * una riga di testo qualunque.
   *
   * Il numero grezzo resta nel `title`: la barra dice a colpo d'occhio, il
   * numero dice esattamente, e chi taratura un algoritmo ha bisogno del secondo.
   */
  meter?: number;
}

/**
 * Shape del draft per il variant `lookupFreeSolo` (autocomplete editabile
 * con dual-field id+nome). Il consumer passa il value come oggetto
 * `{id, name}`, l'onCommit lo riceve nella stessa shape e decide se
 * inviare al backend l'id (selezione esistente) o il nome (free-text →
 * find-or-create lato server). `id === 0` significa "nessuna selezione,
 * solo testo libero".
 */
export interface LookupFreeSoloValue {
  id: number;
  name: string;
}

export type FieldVariant =
  | { kind: 'text' }
  | { kind: 'textarea'; rows?: number }
  /**
   * Input numerico con `min`/`max` opzionali. Non ancora usato dai
   * call site attuali (i campi numerici del CRM sono `string | null`
   * per convenzione Liferay), ma supportato per uso futuro tipico:
   * quantita', prezzi, importi, percentuali.
   */
  | { kind: 'number'; min?: number; max?: number }
  | { kind: 'date' }
  | { kind: 'datetime' }
  | { kind: 'select'; options: { value: string; label: string }[] }
  | { kind: 'switch' }
  /**
   * Autocomplete asincrono su una chiave.
   *
   * `popperClassName` e `loadingText` esistono per una ragione sola: il pannello
   * dei risultati vive in un **portale**, agganciato al body, quindi il CSS del
   * chiamante non lo raggiunge — e il testo di attesa deve poter nominare la
   * fonte («cerco sul gestionale…»), che il core non conosce. Entrambi
   * facoltativi: senza, il pannello prende la larghezza del campo e l'attesa usa
   * il testo generico.
   */
  | {
      kind: 'lookup';
      fetcher: (query: string) => Promise<LookupOption[]>;
      displayLabel: string;
      popperClassName?: string;
      loadingText?: string;
    }
  /**
   * Lookup con freeSolo: l'utente può selezionare un'opzione dall'autocomplete
   * (ricavando id) oppure digitare testo libero (id=0). value e draft sono
   * `LookupFreeSoloValue`; onCommit riceve la stessa shape. Tipico per
   * lookup table con find-or-create lato server (es. PersonSource).
   */
  | { kind: 'lookupFreeSolo'; fetcher: (query: string) => Promise<LookupOption[]> };

/**
 * Tipo unione del draft/value gestito dalla state machine di EditableField.
 *
 * Storicamente era una scalar union (`string | number | boolean | null`).
 * L'aggiunta di `LookupFreeSoloValue` (variant lookupFreeSolo) ha allargato
 * il tipo a includere anche un object record. Conseguenza pratica: ogni
 * call-site che dichiarava `validate`/`onCommit` con la signature scalar
 * ha dovuto passare a `EditableValue` (vedi EmailListEditor, PhoneListEditor,
 * MessageInbox).
 *
 * Estendere ancora questa union (es. nuova variant con shape diverso) ha
 * blast radius su TUTTI i call-site con handler tipizzati strettamente —
 * TS rivela i siti, ma l'effort manuale e' lineare nel numero di consumer.
 * Prima di aggiungere un nuovo membro all'union, valutare se la nuova
 * variant puo' essere modellata come scalar (es. id numerico) e tenere il
 * dato strutturato dentro la `FieldVariant` invece che nel value.
 */
export type EditableValue = string | number | boolean | null | LookupFreeSoloValue;

/** Type guard: distingue `LookupFreeSoloValue` dai tipi scalari di EditableValue. */
function isLookupFreeSoloValue(v: EditableValue): v is LookupFreeSoloValue {
  return typeof v === 'object' && v !== null && 'id' in v && 'name' in v;
}

/**
 * Eq strutturale per `EditableValue`: gestisce sia scalari (===) sia
 * oggetti `LookupFreeSoloValue`. Necessario perché il consumer del
 * variant `lookupFreeSolo` tipicamente passa un oggetto literal a ogni
 * render (`value={{id, name}}`), quindi la reference equality fallirebbe
 * anche quando i contenuti coincidono — e il no-op check del commit
 * sparerebbe roundtrip inutili.
 */
function editableValuesEqual(a: EditableValue, b: EditableValue): boolean {
  if (a === b) return true;
  if (isLookupFreeSoloValue(a) && isLookupFreeSoloValue(b)) {
    return a.id === b.id && a.name === b.name;
  }
  return false;
}

export interface EditableFieldProps {
  value: EditableValue;
  variant: FieldVariant;
  /** Chiamata al commit. Deve rigettare su errore (usare .unwrap() sul thunk). */
  onCommit: (newValue: EditableValue) => Promise<void>;
  /** Validazione client. Ritorna messaggio errore (stringa i18n o literal) oppure null. */
  validate?: (value: EditableValue) => string | null;
  required?: boolean;
  placeholder?: string;
  /** Mostrato in view mode quando value e' null/empty. Default '—'. */
  emptyLabel?: string;
  /** Se true, mostra solo view mode, non entra mai in edit. */
  readOnly?: boolean;
  /** Etichetta accessibile (aria-label). */
  label?: string;
  /** Per textarea: quale tasto triggera commit. Default 'ctrlEnter' (Enter inserisce newline). */
  multilineCommitOn?: 'enter' | 'ctrlEnter';
  /**
   * Se true, in view mode rimuove l'affordance visiva di edit (border al
   * hover, background al hover, padding extra, icona pencil). Il doppio
   * click per entrare in edit funziona ancora. Pensato per i casi in cui
   * l'EditableField vive dentro una cella di tabella o un altro
   * contenitore con stile proprio dove l'affordance "campo editabile" è
   * di troppo (es. QueueList colonna name).
   */
  flat?: boolean;
  /**
   * Se settato, sovrascrive il rendering automatico del value in view mode
   * con questa stringa (es. "Sì" invece di "true", "Legno" invece di "WOOD").
   * In edit mode si usa comunque il `value` raw — la sovrascrittura vale solo
   * per la presentazione. Usato per i MessageInfo con label boolean/enum/
   * numerico-unit dove il backend persiste raw ma la UI mostra localizzato.
   */
  displayOverride?: string;
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type State =
  | { mode: 'view' }
  | { mode: 'edit'; draft: EditableValue; clientError: string | null }
  | { mode: 'saving'; draft: EditableValue }
  | { mode: 'serverError'; draft: EditableValue; serverError: string };

type Action =
  | { type: 'enterEdit'; value: EditableValue }
  | { type: 'setDraft'; draft: EditableValue; clientError: string | null }
  | { type: 'startSave' }
  | { type: 'saveSuccess' }
  | { type: 'saveError'; message: string }
  | { type: 'cancel' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'enterEdit':
      if (state.mode !== 'view') return state;
      return { mode: 'edit', draft: action.value, clientError: null };
    case 'setDraft':
      if (state.mode !== 'edit' && state.mode !== 'serverError') return state;
      return { mode: 'edit', draft: action.draft, clientError: action.clientError };
    case 'startSave':
      if (state.mode !== 'edit') return state;
      return { mode: 'saving', draft: state.draft };
    case 'saveSuccess':
      return { mode: 'view' };
    case 'saveError':
      if (state.mode !== 'saving') return state;
      return { mode: 'serverError', draft: state.draft, serverError: action.message };
    case 'cancel':
      return { mode: 'view' };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Helper: format valore per view mode
// ---------------------------------------------------------------------------

function formatDisplay(value: EditableValue, variant: FieldVariant, emptyLabel: string, translate: (key: string) => string): string {
  if (variant.kind === 'lookupFreeSolo') {
    if (isLookupFreeSoloValue(value)) return value.name || emptyLabel;
    return emptyLabel;
  }
  if (value === null || value === undefined || value === '') return emptyLabel;
  if (variant.kind === 'select') {
    const opt = variant.options.find((o) => o.value === value);
    return opt ? translate(opt.label) : String(value);
  }
  if (variant.kind === 'lookup') return variant.displayLabel || String(value);
  if (variant.kind === 'switch') return value ? '✓' : '—';
  if (variant.kind === 'date' && typeof value === 'string') return value.split('T')[0];
  if (variant.kind === 'datetime' && typeof value === 'string') {
    // value = ISO UTC dal backend; mostrato in ora locale (coerente con liste e
    // accordion che usano lo stesso formatDateTime).
    try {
      return formatDateTime(value);
    } catch {
      return value;
    }
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Lookup sub-component (usa MUI Autocomplete + fetcher asincrono)
// ---------------------------------------------------------------------------

interface LookupInputProps {
  variant: Extract<FieldVariant, { kind: 'lookup' }>;
  draft: EditableValue;
  onChange: (newDraft: EditableValue) => void;
  onBlur: () => void;
  isSaving: boolean;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

function LookupInput({ variant, draft, onChange, onBlur, isSaving, error, inputRef }: LookupInputProps) {
  // Destructure: evita di mettere `variant` literal nelle deps del hook
  // (identity cambia ogni render del parent → refetch continuo).
  const { t, i18n } = useTranslation();

  const { fetcher, displayLabel, popperClassName, loadingText } = variant;
  // `useAsyncAutocomplete` incapsula il pattern MUI async (inputValue
  // controlled, guard reason='input', filterOptions identita', debounce).
  // initialQuery = displayLabel → all'apertura l'utente vede il valore
  // corrente nella textbox e puo' backspacarlo per modificarlo.
  const { options, loading, autocompleteProps } = useAsyncAutocomplete<LookupOption>({
    fetcher,
    initialQuery: displayLabel,
  });

  // `draft` E' gia' la chiave: coercerlo a numero rende NaN un codice articolo, e
  // `isOptionEqualToValue` non ritrova piu' la selezione corrente. La stringa vuota va
  // trattata come "nessuna selezione" e non come una chiave: un `{id: '', label: ''}`
  // comparirebbe nel campo come una riga fantasma.
  const selectedOption =
    options.find((o) => o.id === draft) ??
    (draft !== null && draft !== ''
      ? { id: draft as number | string, label: displayLabel }
      : null);

  return (
    <Autocomplete
      {...autocompleteProps}
      getOptionLabel={(o) => o.label}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.meter !== undefined && (
            <span
                className={`lookup-meter lookup-meter--${meterStep(option.meter)}`}
                title={option.meter.toLocaleString(i18n.language.replace(/_/g, '-'), {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}
            >
              <span className="lookup-meter__fill" />
            </span>
          )}
          {option.label}
        </li>
      )}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={selectedOption}
      onChange={(_, newVal) => onChange(newVal ? newVal.id : null)}
      onBlur={onBlur}
      disabled={isSaving}
      size="small"
      fullWidth
      loading={loading}
      loadingText={loadingText ?? t('editableField.loading')}
      slotProps={
        popperClassName
          ? {
              popper: {
                className: popperClassName,
                // Il bordo sinistro del pannello resta incollato a quello del
                // campo. Il pannello e' piu' largo del campo che lo apre, e
                // senza questo Popper lo sposta verso sinistra per non uscire
                // dalla finestra: il risultato sembra centrato, e l'occhio
                // perde il punto di partenza mentre scorre l'elenco.
                //
                // Si disattiva SOLO `preventOverflow`, che e' quello che
                // sposta. `flip` resta attivo: in fondo a una tabella lunga il
                // pannello deve poter aprirsi verso l'alto.
                placement: 'bottom-start',
                modifiers: [{ enabled: false, name: 'preventOverflow' }],
              },
            }
          : undefined
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          error={!!error}
          helperText={error ?? undefined}
          slotProps={{
            // SI PARTE DA `params.slotProps`, non da un oggetto nuovo.
            //
            // `{...params}` qui sopra porta gia' `slotProps` con TRE slot:
            // `input`, `htmlInput` e `inputLabel`. Passandone uno nuovo con il
            // solo `input` si sostituisce l'intero oggetto, e `htmlInput` — che
            // porta i props e il ref del vero elemento <input> — sparisce.
            // A schermo il campo sembra normale e la console dice
            // "MUI: Unable to find the input element", perche' l'Autocomplete
            // non riesce piu' a raggiungerlo.
            ...params.slotProps,
            // MUI 9: i props dell'input stanno in `params.slotProps.input`, non
            // piu' in `params.InputProps`. Lo spinner va aggiunto a mano — con
            // `loading` MUI cambia il testo del pannello ma non mette nessun
            // segnale nel campo, che e' dove l'occhio di chi digita gia' si
            // trova.
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={16} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// LookupFreeSolo sub-component — thin wrapper su DualFieldAutocomplete
// ---------------------------------------------------------------------------

interface LookupFreeSoloInputProps {
  variant: Extract<FieldVariant, { kind: 'lookupFreeSolo' }>;
  draft: EditableValue;
  onChange: (newDraft: EditableValue) => void;
  onBlur: () => void;
  isSaving: boolean;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * Wrapper che adatta il draft scalar `EditableValue` (cast a
 * `LookupFreeSoloValue`) al contratto di `DualFieldAutocomplete`.
 * Tutta la logica del combobox freeSolo + hook decapitato + 3-rami
 * onChange vive nel componente shared — qui solo il narrowing tipi
 * specifico di EditableField (size small + helperText da string|null).
 */
function LookupFreeSoloInput({ variant, draft, onChange, onBlur, isSaving, error, inputRef }: LookupFreeSoloInputProps) {
  const draftValue = draft as LookupFreeSoloValue;
  return (
    <DualFieldAutocomplete
      value={draftValue}
      onChange={onChange}
      fetcher={variant.fetcher}
      size="small"
      fullWidth
      disabled={isSaving}
      error={!!error}
      helperText={error ?? undefined}
      onBlur={onBlur}
      inputRef={inputRef}
    />
  );
}

// ---------------------------------------------------------------------------
// Componente principale
// ---------------------------------------------------------------------------

function EditableFieldInner({
  value,
  variant,
  onCommit,
  validate,
  required,
  placeholder,
  emptyLabel = '—',
  displayOverride,
  readOnly = false,
  label,
  multilineCommitOn = 'ctrlEnter',
  flat = false,
}: EditableFieldProps) {
  const [state, dispatch] = useReducer(reducer, { mode: 'view' });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  // Auto-focus quando entriamo in edit/serverError
  useEffect(() => {
    if ((state.mode === 'edit' || state.mode === 'serverError') && inputRef.current) {
      inputRef.current.focus();
      // Select-all per text/textarea/number
      if ('select' in inputRef.current && typeof inputRef.current.select === 'function') {
        inputRef.current.select();
      }
    }
  }, [state.mode]);

  const runValidation = useCallback(
    (draft: EditableValue): string | null => {
      if (required) {
        if (draft === null || draft === undefined || draft === '') {
          return t('editableField.required');
        }
        // lookupFreeSolo: required passa se id>0 OPPURE name non vuoto.
        if (isLookupFreeSoloValue(draft) && draft.id === 0 && draft.name.trim() === '') {
          return t('editableField.required');
        }
      }
      if (validate) return validate(draft);
      return null;
    },
    [required, validate, t]
  );

  const commit = useCallback(
    async (draft: EditableValue) => {
      // No-op: draft uguale al valore corrente, non facciamo nulla.
      // Eq strutturale per gestire sia scalari sia LookupFreeSoloValue
      // (che sarebbe sempre "diverso" via reference eq al re-render parent).
      if (editableValuesEqual(draft, value)) {
        dispatch({ type: 'cancel' });
        return;
      }
      const clientError = runValidation(draft);
      if (clientError) {
        dispatch({ type: 'setDraft', draft, clientError });
        return;
      }
      dispatch({ type: 'startSave' });
      try {
        await onCommit(draft);
        dispatch({ type: 'saveSuccess' });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        dispatch({ type: 'saveError', message: message || t('editableField.saveError') });
      }
    },
    [value, onCommit, runValidation, t]
  );

  // =========================================================================
  // VIEW MODE
  // =========================================================================
  if (state.mode === 'view') {
    const handleEnterEdit = () => {
      if (readOnly) return;
      dispatch({ type: 'enterEdit', value });
    };
    return (
      <div
        className={`editable-field editable-field--view ${readOnly ? 'editable-field--readonly' : ''} ${flat ? 'editable-field--flat' : ''}`}
        onDoubleClick={handleEnterEdit}
        onKeyDown={(e) => {
          if (readOnly) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleEnterEdit();
          }
        }}
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? -1 : 0}
        aria-label={label ?? placeholder}
        title={readOnly ? undefined : t('editableField.doubleClickToEdit')}
      >
        <span className={`editable-field__value ${variant.kind === 'textarea' ? 'editable-field__value--multiline' : ''}`}>{
          displayOverride !== undefined && value !== null && value !== '' && value !== undefined
            ? displayOverride
            : formatDisplay(value, variant, emptyLabel, t)
        }</span>
        {/* Resa anche in sola lettura: li' resta invisibile (vedi
            `.editable-field--readonly`), ma continua a occupare il suo posto —
            senza, il valore di un campo readonly finisce piu' a destra di
            quelli editabili e la riga risulta disallineata. */}
        <EditIcon className="editable-field__icon" fontSize="inherit" />
      </div>
    );
  }

  // =========================================================================
  // EDIT / SAVING / SERVER_ERROR — render input
  // =========================================================================
  const isSaving = state.mode === 'saving';
  const isServerError = state.mode === 'serverError';
  const draft = state.draft;
  const clientError = state.mode === 'edit' ? state.clientError : null;
  const serverError = state.mode === 'serverError' ? state.serverError : null;
  const errorMsg = clientError || serverError;

  const handleChange = (newDraft: EditableValue) => {
    const err = runValidation(newDraft);
    dispatch({ type: 'setDraft', draft: newDraft, clientError: err });
  };

  const handleBlur = () => {
    if (isSaving || isServerError) return;
    if (state.mode === 'edit' && !state.clientError) {
      void commit(state.draft);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      dispatch({ type: 'cancel' });
      return;
    }
    if (e.key === 'Enter') {
      // Textarea: gestione speciale per newline vs commit
      if (variant.kind === 'textarea') {
        const isCommitKey =
          multilineCommitOn === 'enter' ? !e.shiftKey : (e.ctrlKey || e.metaKey);
        if (isCommitKey) {
          e.preventDefault();
          if (state.mode === 'edit' && !state.clientError) void commit(state.draft);
        }
        // Altrimenti, lascia passare il newline
        return;
      }
      // Single-line: Enter committa sempre
      e.preventDefault();
      if (state.mode === 'edit' && !state.clientError) void commit(state.draft);
    }
  };

  const containerClass = [
    'editable-field',
    'editable-field--edit',
    isSaving && 'editable-field--saving',
    isServerError && 'editable-field--error',
  ]
    .filter(Boolean)
    .join(' ');

  let inputEl: ReactNode;
  switch (variant.kind) {
    case 'text':
      inputEl = (
        <TextField
          inputRef={inputRef}
          value={(draft as string | null) ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          disabled={isSaving}
          placeholder={placeholder}
          error={!!errorMsg}
          helperText={errorMsg ?? undefined}
          aria-invalid={!!errorMsg}
        />
      );
      break;
    case 'textarea':
      inputEl = (
        <TextField
          inputRef={inputRef}
          value={(draft as string | null) ?? ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          multiline
          minRows={variant.rows ?? 3}
          disabled={isSaving}
          placeholder={placeholder}
          error={!!errorMsg}
          helperText={errorMsg ?? undefined}
        />
      );
      break;
    case 'number':
      inputEl = (
        <TextField
          inputRef={inputRef}
          type="number"
          value={draft === null || draft === undefined ? '' : String(draft)}
          onChange={(e) => handleChange(e.target.value === '' ? null : Number(e.target.value))}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          disabled={isSaving}
          placeholder={placeholder}
          error={!!errorMsg}
          helperText={errorMsg ?? undefined}
          slotProps={{ htmlInput: { min: variant.min, max: variant.max } }}
        />
      );
      break;
    case 'date':
      inputEl = (
        <TextField
          inputRef={inputRef}
          type="date"
          value={(draft as string | null) ?? ''}
          onChange={(e) => handleChange(e.target.value || null)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          disabled={isSaving}
          error={!!errorMsg}
          helperText={errorMsg ?? undefined}
        />
      );
      break;
    case 'datetime':
      // L'input lavora in "YYYY-MM-DDTHH:mm"; il draft committato e' ISO backend
      // ("...:00Z") via datetimeLocalToIso. isoToDatetimeLocal gestisce sia valori
      // ISO sia gia'-locali (idempotente). Unico punto di conversione: vedi
      // formatDate.ts (datetimeLocalToIso / isoToDatetimeLocal).
      inputEl = (
        <TextField
          inputRef={inputRef}
          type="datetime-local"
          value={isoToDatetimeLocal(draft as string | null)}
          onChange={(e) => handleChange(datetimeLocalToIso(e.target.value))}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="small"
          fullWidth
          disabled={isSaving}
          error={!!errorMsg}
          helperText={errorMsg ?? undefined}
        />
      );
      break;
    case 'select':
      inputEl = (
        <Select
          inputRef={inputRef}
          value={(draft as string | null) ?? ''}
          onChange={(e: SelectChangeEvent<string>) => {
            const v = e.target.value;
            // Select auto-commit: MUI chiude il popup e potrebbe non generare blur
            handleChange(v);
            const err = runValidation(v);
            if (!err && v !== value) void commit(v);
          }}
          onBlur={handleBlur}
          size="small"
          fullWidth
          disabled={isSaving}
          error={!!errorMsg}
        >
          {variant.options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {t(opt.label)}
            </MenuItem>
          ))}
        </Select>
      );
      break;
    case 'switch':
      inputEl = (
        <Switch
          checked={!!draft}
          onChange={(e) => {
            const v = e.target.checked;
            handleChange(v);
            // Auto-commit on toggle
            void commit(v);
          }}
          disabled={isSaving}
        />
      );
      break;
    case 'lookup':
      inputEl = (
        <LookupInput
          variant={variant}
          draft={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          isSaving={isSaving}
          error={errorMsg}
          inputRef={inputRef}
        />
      );
      break;
    case 'lookupFreeSolo':
      inputEl = (
        <LookupFreeSoloInput
          variant={variant}
          draft={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          isSaving={isSaving}
          error={errorMsg}
          inputRef={inputRef}
        />
      );
      break;
  }

  return (
    <div className={containerClass}>
      {inputEl}
      {isSaving && <CircularProgress size={16} className="editable-field__spinner" />}
    </div>
  );
}

export const EditableField = memo(EditableFieldInner);
